import { ClickAwayListener } from "@mui/base";
import { ListItem, Menu, MenuItem, ListItemContent } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { useVenueZonesByClimbType } from "@/hooks/routes";
import { useCurrentVenueQuery } from "@/hooks/venues";

import getSetTypeChoices from "../TaskForm/getSetTypeChoices";
import { useCalendar, useVirtualElement } from "./CalendarContext";
import TaskSetMenuItems from "./TaskSetMenuItems";

export const CalendarContextMenu = () => {
  const { contextMenu, setContextMenu, setEditingSetUuid } = useCalendar();
  const virtualElement = useVirtualElement(contextMenu);

  if (contextMenu === null) return null;

  let items: React.ReactNode = null;
  if (contextMenu.date) {
    items = <CreateNewSetMenuItems date={contextMenu.date} onClose={() => setContextMenu(null)} />;
  } else if (contextMenu.taskSet) {
    items = (
      <TaskSetMenuItems
        taskSet={contextMenu.taskSet}
        onClose={() => setContextMenu(null)}
        onEdit={() => contextMenu.taskSet && setEditingSetUuid(contextMenu.taskSet.uuid)}
      />
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setContextMenu(null)}>
      <Menu size="sm" anchorEl={virtualElement} open placement="bottom-start">
        {items}
      </Menu>
    </ClickAwayListener>
  );
};

export const CreateNewSetMenuItems = ({
  onClose,
  date,
}: {
  onClose: () => void;
  date?: string;
}) => {
  const { data: venue } = useCurrentVenueQuery();
  const { data: zones } = useVenueZonesByClimbType(venue?.uuid, "BOULDER");
  const { data: sportZones } = useVenueZonesByClimbType(venue?.uuid, "SPORT");
  const { setNewSetValues } = useCalendar();

  const typeChoices = useMemo(
    () => getSetTypeChoices(venue, zones, sportZones),
    [venue, zones, sportZones],
  );

  const navigate = useNavigate();

  return (
    <>
      <ListItem>
        <ListItemContent sx={{ color: "neutral.600", fontWeight: "bold" }}>
          Create a set
        </ListItemContent>
      </ListItem>
      {typeChoices.map(({ name, value }) => (
        <MenuItem
          key={value}
          onClick={() => {
            onClose();
            setNewSetValues({ date, type: value });
          }}
        >
          {name}
        </MenuItem>
      ))}
      <MenuItem
        key="comeptition"
        onClick={() => {
          onClose();
          navigate({
            from: "/gym/$venueUuid",
            to: "/gym/$venueUuid/events/create",
            params: true,
            search: { startDate: date },
          });
        }}
      >
        Competition
      </MenuItem>
    </>
  );
};
